import { Button, Text } from "@/elements"
import { graphql } from "@/gql"
import { CheckIgStatusDocument, GenerateInstagramVerificationCode, GetSocialAccountsDocument } from "@/gql/graphql"
import { copyToClipboard } from "@/utils/copyToClipboard"
import { DocumentNode, useQuery } from "@apollo/client"
import Link from "next/link"

import { useEffect, useState } from "react"
import { useInstagramFlowStore } from "./instagramStore"

interface Props {
  setStep: (step: number) => void
  instagramCodeData: GenerateInstagramVerificationCode
  socialAccountId: string | undefined
  onVerifyCallback: (value: string) => void
  refetchQueries: DocumentNode[]
}

graphql(`
  query CheckIgStatus($socialAccountId: ID!) {
    socialAccount(id: $socialAccountId) {
      __typename
      id
      provider
      profileUrl
      username
      verified
    }
  }
`)

export const CopyCodeStep: React.FC<Props> = ({
  setStep,
  instagramCodeData,
  socialAccountId,
  onVerifyCallback,
  refetchQueries,
}) => {
  const { data, stopPolling, client } = useQuery(CheckIgStatusDocument, {
    variables: { socialAccountId: socialAccountId! },
    skip: !socialAccountId,
    pollInterval: 3000,
  })
  const { setHeader, setFooter } = useInstagramFlowStore()
  const [isVerified, setIsVerified] = useState(false)

  useEffect(() => {
    if (data?.socialAccount.verified) {
      setIsVerified(true)
      onVerifyCallback(instagramCodeData.username)
      client.refetchQueries({ include: refetchQueries })
      stopPolling()
    }
  }, [client, data?.socialAccount.verified, stopPolling])

  const [copied, setCopied] = useState(false)

  useEffect(() => {
    setHeader({
      title: "Step 2/2",
      subtitle: `DM this code to @${instagramCodeData.recipientUsername} on Instagram`,
      variant: "progress",
      fixed: true,
    })
    setFooter({
      fixed: true,
      primaryButton: {
        children: isVerified ? "Done" : "Open Instagram",
        onClick: () => {
          if (isVerified) {
            setStep(0)
          } else {
            copyToClipboard(instagramCodeData.code, { successMessage: "Copied code" })
            window.open(instagramCodeData?.recipientInstagramProfileLink, "_blank")
          }
        },
      },
    })
  }, [isVerified, instagramCodeData?.recipientInstagramProfileLink, instagramCodeData.recipientUsername])

  return (
    <>
      <div className="h-full">
        <div className="relative flex flex-col items-center gap-y-4 px-14 pt-6">
          <div
            onClick={() => {
              copyToClipboard(instagramCodeData.code)
            }}
            className="flex w-full cursor-pointer items-center justify-center rounded-md bg-bg-3 py-4">
            <Text className="w-fit select-none text-fg-2">
              {instagramCodeData.code.slice(0, 10) +
                "..." +
                instagramCodeData.code.slice(instagramCodeData.code.length - 5, instagramCodeData.code.length)}
            </Text>
          </div>
          {isVerified ? (
            <div className="flex items-center gap-x-2 rounded-md bg-green-light px-4 py-2">
              <Text variant="body3" className="text-[#098506]">
                @{instagramCodeData.username} verified!
              </Text>
            </div>
          ) : copied ? (
            <Text>Waiting for verification...</Text>
          ) : (
            <Button
              onClick={() => {
                setCopied(true)
                copyToClipboard(instagramCodeData.code)
              }}
              variant="transparent">
              Copy code
            </Button>
          )}
        </div>
      </div>
    </>
  )
}
